import React from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';
import { ArrowLeft, FileText, Scale, AlertTriangle, CheckCircle, Ban, RefreshCw, Shield, Mail } from 'lucide-react';

const TermsOfServicePage = () => {
  const lastUpdated = "March 2026";
  const effectiveDate = "March 1, 2026";

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1F3C4F] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-10 h-10 text-[#1F3C4F]" />
              <h1 className="text-4xl font-bold text-slate-900">Terms of Service</h1>
            </div>
            <p className="text-slate-500">Last Updated: {lastUpdated}</p>
            <p className="text-slate-500">Effective Date: {effectiveDate}</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg prose-slate max-w-none">
            
            {/* Introduction */}
            <section className="mb-12 p-6 bg-slate-50 rounded-xl">
              <p className="text-lg text-slate-600 leading-relaxed m-0">
                Welcome to SPT Digital Marketing LLC. These Terms of Service ("Terms") govern your access 
                to and use of our website at <strong>sptdigitalmarketing.com</strong> and any services 
                provided by SPT Digital Marketing LLC ("Company," "we," "our," or "us"). By accessing or 
                using our website and services, you agree to be bound by these Terms.
              </p>
            </section>

            {/* Acceptance */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">1. Acceptance of Terms</h2>
              </div>
              
              <p className="text-slate-600">
                By accessing or using our website, you acknowledge that you have read, understood, and 
                agree to be bound by these Terms of Service and our <Link to="/privacy-policy" className="text-[#1F3C4F] hover:underline">Privacy Policy</Link>. 
                If you do not agree to these Terms, please do not use our website or services.
              </p>
              <p className="text-slate-600">
                We reserve the right to modify these Terms at any time. Changes will be effective 
                immediately upon posting to the website. Your continued use of the website after any 
                changes indicates your acceptance of the modified Terms.
              </p>
            </section>

            {/* Services Description */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900">2. Description of Services</h2>
              <p className="text-slate-600">
                SPT Digital Marketing LLC provides digital marketing services, including but not limited to:
              </p>
              <ul className="text-slate-600 space-y-2">
                <li>Website design and development</li>
                <li>Web application development</li>
                <li>Search engine optimization (SEO)</li>
                <li>Content marketing and strategy</li>
                <li>Social media marketing</li>
                <li>Digital advertising management</li>
                <li>Brand development and strategy</li>
                <li>Marketing consultation services</li>
              </ul>
              <p className="text-slate-600">
                The specific services, deliverables, timelines, and fees will be outlined in a separate 
                service agreement or proposal for each client engagement.
              </p>
            </section>

            {/* User Responsibilities */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="w-6 h-6 text-[#1F3C4F]" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">3. User Responsibilities</h2>
              </div>
              
              <p className="text-slate-600">When using our website and services, you agree to:</p>
              <ul className="text-slate-600 space-y-2">
                <li>Provide accurate and complete information when contacting us or using our services</li>
                <li>Use the website only for lawful purposes and in accordance with these Terms</li>
                <li>Not engage in any activity that could damage, disable, or impair our website</li>
                <li>Not attempt to gain unauthorized access to any part of our website or systems</li>
                <li>Not use any automated systems or software to extract data from our website</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section className="mb-12 p-6 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">4. Intellectual Property Rights</h2>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-4">Our Content</h3>
              <p className="text-slate-600">
                All content on this website, including text, graphics, logos, images, audio, video, 
                software, and other materials ("Content"), is owned by or licensed to SPT Digital 
                Marketing LLC and is protected by copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4">Limited License</h3>
              <p className="text-slate-600">
                You are granted a limited, non-exclusive, non-transferable license to access and view 
                the Content on this website for personal, non-commercial use. You may not:
              </p>
              <ul className="text-slate-600 space-y-1">
                <li>Reproduce, distribute, or publicly display our Content without written permission</li>
                <li>Modify or create derivative works based on our Content</li>
                <li>Use our Content for commercial purposes without authorization</li>
                <li>Remove any copyright or proprietary notices from our Content</li>
              </ul>

              <h3 className="text-lg font-semibold text-slate-800 mt-4">Client Work</h3>
              <p className="text-slate-600">
                Intellectual property rights for work created for clients will be addressed in individual 
                service agreements. Generally, upon full payment, clients receive ownership or licensing 
                rights to their custom deliverables as specified in the agreement.
              </p>
            </section>

            {/* Prohibited Activities */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Ban className="w-6 h-6 text-red-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">5. Prohibited Activities</h2>
              </div>
              
              <p className="text-slate-600">You agree not to engage in any of the following activities:</p>
              <ul className="text-slate-600 space-y-2">
                <li>Violating any applicable laws, regulations, or third-party rights</li>
                <li>Transmitting any viruses, malware, or other harmful code</li>
                <li>Attempting to interfere with or disrupt our website or servers</li>
                <li>Impersonating any person or entity or misrepresenting your affiliation</li>
                <li>Collecting or harvesting personal information from our website</li>
                <li>Using the website for any fraudulent or deceptive purposes</li>
                <li>Sending unsolicited communications or spam</li>
                <li>Engaging in any activity that could harm our reputation or business</li>
              </ul>
            </section>

            {/* Disclaimer of Warranties */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">6. Disclaimer of Warranties</h2>
              </div>
              
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-slate-700 text-sm m-0">
                  OUR WEBSITE AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES 
                  OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES 
                  OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
              </div>
              
              <p className="text-slate-600 mt-4">We do not warrant that:</p>
              <ul className="text-slate-600 space-y-2">
                <li>Our website will be uninterrupted, secure, or error-free</li>
                <li>Any defects or errors will be corrected</li>
                <li>Our website or servers are free of viruses or harmful components</li>
                <li>The results of using our services will meet your expectations</li>
              </ul>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900">7. Limitation of Liability</h2>
              <p className="text-slate-600">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, SPT DIGITAL MARKETING LLC AND ITS OFFICERS, 
                DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF 
                PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF OUR WEBSITE 
                OR SERVICES.
              </p>
              <p className="text-slate-600">
                Our total liability for any claims arising from these Terms or your use of our website 
                shall not exceed the amount you paid to us, if any, during the twelve (12) months 
                preceding the claim.
              </p>
            </section>

            {/* Indemnification */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900">8. Indemnification</h2>
              <p className="text-slate-600">
                You agree to indemnify, defend, and hold harmless SPT Digital Marketing LLC and its 
                officers, directors, employees, agents, and affiliates from and against any claims, 
                liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) 
                arising out of or related to:
              </p>
              <ul className="text-slate-600 space-y-2">
                <li>Your use of our website or services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Any content you submit or transmit through our website</li>
              </ul>
            </section>

            {/* Termination */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <RefreshCw className="w-6 h-6 text-[#1F3C4F]" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">9. Termination</h2>
              </div>
              
              <p className="text-slate-600">
                We reserve the right to terminate or suspend your access to our website at any time, 
                without prior notice or liability, for any reason, including if you breach these Terms.
              </p>
              <p className="text-slate-600">
                Upon termination, your right to use the website will immediately cease. Provisions of 
                these Terms that by their nature should survive termination shall survive, including 
                ownership provisions, warranty disclaimers, indemnification, and limitations of liability.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900">10. Governing Law & Jurisdiction</h2>
              <p className="text-slate-600">
                These Terms shall be governed by and construed in accordance with the laws of the 
                State of North Carolina, United States, without regard to its conflict of law provisions.
              </p>
              <p className="text-slate-600">
                Any disputes arising from these Terms or your use of our website shall be resolved 
                exclusively in the state or federal courts located in Brunswick County, North Carolina. 
                You consent to the personal jurisdiction of such courts.
              </p>
            </section>

            {/* Miscellaneous */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900">11. Miscellaneous</h2>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-4">Entire Agreement</h3>
              <p className="text-slate-600">
                These Terms, together with our Privacy Policy and any service agreements, constitute 
                the entire agreement between you and SPT Digital Marketing LLC regarding your use of 
                our website.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4">Severability</h3>
              <p className="text-slate-600">
                If any provision of these Terms is found to be invalid or unenforceable, the remaining 
                provisions shall remain in full force and effect.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4">Waiver</h3>
              <p className="text-slate-600">
                Our failure to enforce any right or provision of these Terms shall not be considered 
                a waiver of such right or provision.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-4">Assignment</h3>
              <p className="text-slate-600">
                You may not assign or transfer these Terms without our prior written consent. We may 
                assign our rights and obligations under these Terms without restriction.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-12 p-6 bg-slate-100 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-[#1F3C4F]" />
                <h2 className="text-2xl font-bold text-slate-900 m-0">12. Contact Information</h2>
              </div>
              
              <p className="text-slate-600">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="mt-4 space-y-2 text-slate-700">
                <p><strong>SPT Digital Marketing LLC</strong></p>
                <p>Email: <a href="mailto:info@sptdigitalmarketing.com" className="text-[#1F3C4F] hover:underline">info@sptdigitalmarketing.com</a></p>
                <p>General Inquiries: <a href="mailto:info@sptdigitalmarketing.com" className="text-[#1F3C4F] hover:underline">info@sptdigitalmarketing.com</a></p>
                <p>Location: Southport, NC</p>
              </div>
            </section>

          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default TermsOfServicePage;
